import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLiff } from '../../contexts/LiffContext';
import { getGroupIdFromUrl } from '../../utils/getGroupIdFromUrl';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { getLiteJwtTokenFromUrlOrStorage } from '../../utils/liteJwtToken';
import {
  payLiteSubscription,
  payLiteSubscriptionEcPay,
  getSubscriptionStatus,
  cancelSubscription,
  type CancelSubscriptionFeedback,
  type PayLiteEcPayInvoice,
} from '../../api/liteSubscriptionClient';
import { logErrorToBackend } from '../../utils/errorLogger';
import { cn } from '@/lib/utils';
import {
  utcToTaipei,
  DAY_MS,
  formatDateTaipeiSlash,
} from '@pos/shared/utils/time';

const FEATURES = [
  '活動發佈',
  '活動結果統計',
  '活動交付清單',
  '客製活動圖文訊息',
] as const;

/** 取消訂閱回饋：分組與選項（與後端 CANCELLATION_REASON_CODES 一致） */
const CANCEL_REASON_SECTIONS: {
  section: string;
  items: { code: string; label: string }[];
}[] = [
  {
    section: '1. 成本與價值 (Cost & Value)',
    items: [
      { code: 'cost_over_budget', label: '價格超出預算' },
      { code: 'low_value', label: '性價比（CP值）不高' },
    ],
  },
  {
    section: '2. 使用狀況 (Usage)',
    items: [
      { code: 'low_usage', label: '使用頻率太低' },
      { code: 'business_ended', label: '業務結束或轉型' },
    ],
  },
  {
    section: '3. 產品功能 (Features & Product)',
    items: [
      { code: 'missing_feature', label: '缺少關鍵功能' },
      { code: 'too_complex', label: '功能太複雜／難以上手' },
    ],
  },
  {
    section: '4. 技術與體驗 (Technical & UX)',
    items: [
      { code: 'unstable', label: '系統不穩定／常有 Bug' },
      { code: 'slow_support', label: '技術支援／客服回應慢' },
    ],
  },
  {
    section: '5. 競爭與替代 (Competition)',
    items: [
      { code: 'switched_competitor', label: '已轉換到競爭對手的服務' },
      { code: 'no_longer_needed', label: '不需要這類服務了' },
    ],
  },
  {
    section: '6. 其他 (Other)',
    items: [
      {
        code: 'taking_break',
        label: '只是暫時休息：我之後可能會回來，現在先暫停一下。',
      },
      { code: 'other', label: '其他原因：（開放式填寫框）' },
    ],
  },
];

/** 可選訂閱方案（已取消免費方案，僅顯示付費方案） */
const PLANS = [
  {
    name: '一般方案',
    slug: 'basic' as const,
    price: 'NT180',
    publishLimit: '活動發佈：每月上限6個',
    isFree: false,
  },
  {
    name: '加值方案',
    slug: 'plus' as const,
    price: 'NT300',
    publishLimit: '活動發佈：每月上限12個',
    isFree: false,
  },
  {
    name: '專業方案',
    slug: 'pro' as const,
    price: 'NT600',
    publishLimit: '活動發佈：每月上限36個',
    isFree: false,
  },
] as const;

const TAPPAY_SDK = 'https://js.tappaysdk.com/sdk/tpdirect/v5.18.0';

declare global {
  interface Window {
    TPDirect?: {
      setupSDK: (appId: number, appKey: string, serverType: string) => void;
      card: {
        setup: (config: {
          fields: {
            number: { element: string; placeholder?: string };
            expirationDate: { element: string; placeholder?: string };
            ccv: { element: string; placeholder?: string };
          };
          styles?: object;
          /** 必傳：TapPay 依此回呼更新欄位驗證狀態，按鈕應依 canGetPrime 解鎖；status 的 0=正確 1=未填/輸入中 2=錯誤，鍵名可能為 expiry 或 expirationDate */
          onUpdate?: (update: {
            canGetPrime?: boolean;
            status?: {
              number?: number;
              expiry?: number;
              expirationDate?: number;
              ccv?: number;
            };
          }) => void;
          /** 設為 true 允許取得 Prime */
          isAllowedToPay?: boolean;
        }) => void;
        /** 取得 TapPay Fields 狀態；呼叫 getPrime 前應先確認 canGetPrime 為 true */
        getTappayFieldsStatus?: () => { canGetPrime: boolean };
        getPrime: (
          cb: (r: { status: number; prime?: string; msg?: string }) => void
        ) => void;
      };
    };
  }
}

export function SubscriptionPlanPage() {
  const { liff } = useLiff();
  const navigate = useNavigate();
  const { suffix } = useParams<{ suffix?: string }>();
  const groupId = getGroupIdFromUrl();
  const isSquareMode = Boolean(
    suffix && suffix.length >= 6 && suffix.length <= 8
  );
  const [payOpen, setPayOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<
    (typeof PLANS)[number] | null
  >(null);
  const [cardholder, setCardholder] = useState({
    name: '',
    phone_number: '',
    email: '',
  });
  const [isPaying, setIsPaying] = useState(false);
  /** 由 TPDirect.card.setup 的 onUpdate 更新，為 true 時才允許呼叫 getPrime */
  const [canGetPrime, setCanGetPrime] = useState(false);
  /** onUpdate 的 status：0=正確 1=未填/輸入中 2=錯誤，用來顯示哪一欄格式不對 */
  const [fieldStatus, setFieldStatus] = useState<{
    number?: number;
    expiry?: number;
    expirationDate?: number;
    ccv?: number;
  }>({});
  /** 節流：canGetPrime=false 時送 client_logs 的上次時間，避免 onUpdate 狂洗 */
  const lastTapPayLogRef = useRef(0);
  /** 防止 TPDirect.card.setup 重複執行（React Strict Mode / LIFF 重掛載會觸發 useEffect 兩次，導致兩組 iframe 打架） */
  const hasSetupRef = useRef(false);
  /** setupSDK 整頁只執行一次；重複呼叫可能讓 SDK 內部狀態錯亂、canGetPrime 異常（依官方建議） */
  const hasSetupSDKRef = useRef(false);
  /** 訂閱狀態：目前方案、已使用／上限、預計更新日、是否已設不續訂 */
  const [status, setStatus] = useState<{
    planSlug: 'free' | 'trial' | 'basic' | 'plus' | 'pro';
    used: number;
    limit: number;
    periodEnd: string | null;
    cancelAtPeriodEnd?: boolean;
    isTrial: boolean;
    trialEndsAt: string | null;
  } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReasons, setCancelReasons] = useState<string[]>([]);
  const [cancelMissingFeature, setCancelMissingFeature] = useState('');
  const [cancelOtherReason, setCancelOtherReason] = useState('');
  /** 付款方式：目前僅提供綠界 */
  const [paymentMethod, setPaymentMethod] = useState<'tappay' | 'ecpay'>(
    'ecpay'
  );
  /** 綠界發票（選填；若填則需 invoiceEmail） */
  const [invoice, setInvoice] = useState<PayLiteEcPayInvoice>({
    invoiceType: 'personal',
    invoiceEmail: '',
  });

  const [searchParams, setSearchParams] = useSearchParams();

  /** Group 傳 groupId，Square 傳 squareSuffix（與 Group 共用同一訂閱） */
  const subscriptionContext = useMemo(
    () =>
      isSquareMode && suffix
        ? { squareSuffix: suffix }
        : groupId
          ? { groupId }
          : undefined,
    [isSquareMode, suffix, groupId]
  );

  const fetchStatus = useCallback(async () => {
    if (!subscriptionContext) return;
    const jwt = getLiteJwtTokenFromUrlOrStorage();
    if (!jwt) return;
    const raw = liff ? await liff.getIDToken() : null;
    const lineIdToken = raw && typeof raw === 'string' ? raw : undefined;
    try {
      const s = await getSubscriptionStatus(
        jwt,
        lineIdToken,
        subscriptionContext
      );
      setStatus(s);
    } catch {
      setStatus(null);
    }
  }, [subscriptionContext, liff]);

  useEffect(() => {
    if (subscriptionContext && getLiteJwtTokenFromUrlOrStorage()) {
      fetchStatus();
    } else {
      setStatus(null);
    }
  }, [subscriptionContext, fetchStatus]);

  /** 綠界付款完成導回：pay=success&orderNumber= → toast 並重整狀態 */
  const hasHandledEcPayReturn = useRef(false);
  useEffect(() => {
    const pay = searchParams.get('pay');
    const orderNumber = searchParams.get('orderNumber');
    if (pay === 'success' && orderNumber && !hasHandledEcPayReturn.current) {
      hasHandledEcPayReturn.current = true;
      toast.success('綠界付款成功');
      fetchStatus();
      const next = new URLSearchParams(searchParams);
      next.delete('pay');
      next.delete('orderNumber');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus]);

  /** 從商家資訊「目前方案」區塊點「取消訂閱」導入時，自動開啟取消訂閱對話框 */
  useEffect(() => {
    if (searchParams.get('openCancel') === '1') {
      setCancelReasons([]);
      setCancelMissingFeature('');
      setCancelOtherReason('');
      setCancelDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openCancel');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const trialDaysLeft = useMemo(() => {
    if (!status?.trialEndsAt || status.planSlug !== 'trial') {
      return null;
    }
    const nowTaipei = new Date(utcToTaipei(new Date().toISOString()));
    const trialEndTaipei = new Date(utcToTaipei(status.trialEndsAt));
    const diffMs = trialEndTaipei.getTime() - nowTaipei.getTime();
    return Math.max(0, Math.ceil(diffMs / DAY_MS));
  }, [status]);

  const handleBack = () => {
    if (isSquareMode && suffix) {
      navigate(`/lite/shop/${suffix}`);
    } else if (groupId) {
      navigate(`/lite/setting?groupId=${encodeURIComponent(groupId)}`);
    } else {
      navigate('/lite');
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelReasons([]);
    setCancelMissingFeature('');
    setCancelOtherReason('');
    setCancelDialogOpen(true);
  };

  const handleToggleCancelReason = (code: string) => {
    setCancelReasons(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleCancelConfirm = async () => {
    const jwt = getLiteJwtTokenFromUrlOrStorage();
    if (!jwt) {
      toast.error('請先登入');
      return;
    }
    if (!subscriptionContext) {
      toast.error('請從群組或商家進入以管理訂閱');
      return;
    }
    setIsCancelling(true);

    // 確保 reasons 是有效的字串陣列，過濾掉無效值
    const validReasons = Array.isArray(cancelReasons)
      ? cancelReasons.filter(
          (r): r is string => typeof r === 'string' && r.trim().length > 0
        )
      : [];

    // 構建 feedback 物件，確保所有值都是有效的
    const feedback: CancelSubscriptionFeedback = {
      reasons: validReasons.length > 0 ? validReasons : undefined,
      missingFeature:
        cancelReasons.includes('missing_feature') && cancelMissingFeature.trim()
          ? String(cancelMissingFeature).trim().slice(0, 500)
          : undefined,
      otherReason:
        cancelReasons.includes('other') && cancelOtherReason.trim()
          ? String(cancelOtherReason).trim().slice(0, 1000)
          : undefined,
    };

    // 檢查是否有任何有效的回饋內容
    const hasAny =
      (feedback.reasons?.length ?? 0) > 0 ||
      (feedback.missingFeature?.length ?? 0) > 0 ||
      (feedback.otherReason?.length ?? 0) > 0;
    try {
      const raw = liff ? await liff.getIDToken() : null;
      const lineIdToken = raw && typeof raw === 'string' ? raw : undefined;
      await cancelSubscription(
        jwt,
        hasAny ? feedback : undefined,
        lineIdToken,
        subscriptionContext
      );
      toast.success('已設定本期結束後不續訂');
      setCancelDialogOpen(false);
      fetchStatus();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '取消訂閱失敗');
      logErrorToBackend(e instanceof Error ? e : new Error(String(e)), {
        context: {
          event: 'cancel_subscription_failed',
          groupId,
          feedback: hasAny ? feedback : null,
        },
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenPay = (plan: (typeof PLANS)[number]) => {
    if (!subscriptionContext) {
      toast.error('請從群組或商家進入以訂閱');
      return;
    }
    if (!getLiteJwtTokenFromUrlOrStorage()) {
      toast.error('請先登入');
      return;
    }
    setSelectedPlan(plan);
    // 僅顯示綠界付款
    setPaymentMethod('ecpay');
    setCardholder({ name: '', phone_number: '', email: '' });
    setInvoice({ invoiceType: 'personal', invoiceEmail: '' });
    setCanGetPrime(false);
    setFieldStatus({});
    setPayOpen(true);
  };

  const loadAndSetupTapPay = useCallback(() => {
    if (!import.meta.env.VITE_TAPPAY_APP_KEY) return;
    setCanGetPrime(false);
    setFieldStatus({});
    lastTapPayLogRef.current = 0;
    const appId = parseInt(import.meta.env.VITE_TAPPAY_APP_ID || '0', 10);
    const appKey = import.meta.env.VITE_TAPPAY_APP_KEY || '';
    // 僅當明確為字串 'false'（不分大小寫）時用 production，其餘（含 "true"、undefined）皆為 sandbox
    const raw = String(
      import.meta.env.VITE_TAPPAY_IS_SANDBOX ?? ''
    ).toLowerCase();
    const isSandbox = raw !== 'false';
    const serverType = isSandbox ? 'sandbox' : 'production';
    if (import.meta.env.DEV) {
      console.warn(
        '[TapPay] serverType=',
        serverType,
        '(VITE_TAPPAY_IS_SANDBOX=',
        import.meta.env.VITE_TAPPAY_IS_SANDBOX,
        ')'
      );
    }

    const run = () => {
      const T = window.TPDirect;
      if (!T) return;
      // setupSDK 整頁只呼叫一次，重複會干擾 SDK 狀態、導致 canGetPrime 異常（TapPay 官方建議）
      if (!hasSetupSDKRef.current) {
        T.setupSDK(appId, appKey, serverType);
        hasSetupSDKRef.current = true;
      }
      // 等待 #card-* 已存在（Dialog 已繪出）
      const tick = () => {
        if (!document.getElementById('card-number')) {
          requestAnimationFrame(tick);
          return;
        }
        // Dialog 可能仍在開啟動畫，延後 150ms 讓 iframe 容器穩定可見後再 setup
        setTimeout(() => {
          if (hasSetupRef.current) return;
          const elNum = document.getElementById('card-number');
          const elExp = document.getElementById('card-expiration-date');
          const elCcv = document.getElementById('card-ccv');
          if (!elNum || !elExp || !elCcv) return;
          hasSetupRef.current = true;
          // 清空容器，避免 SPA 重掛載時舊 iframe 殘留導致雙實體打架
          elNum.innerHTML = '';
          elExp.innerHTML = '';
          elCcv.innerHTML = '';
          T.card.setup({
            fields: {
              number: {
                element: '#card-number',
                placeholder: '**** **** **** ****',
              },
              expirationDate: {
                element: '#card-expiration-date',
                placeholder: 'MM / YY',
              },
              ccv: { element: '#card-ccv', placeholder: '***' },
            },
            styles: {},
            isAllowedToPay: true,
            // 未設定 isAllowedTypes / allowedCardTypes，Visa 等皆允許
            onUpdate: function (update) {
              setCanGetPrime(update.canGetPrime === true);
              setFieldStatus(update.status || {});
              if (
                update.canGetPrime !== true &&
                Date.now() - lastTapPayLogRef.current > 10000
              ) {
                lastTapPayLogRef.current = Date.now();
                logErrorToBackend(new Error('TapPay canGetPrime=false'), {
                  severity: 'info',
                  skipConsole: true,
                  context: {
                    event: 'tappay_cangetprime_false',
                    status: update.status,
                    groupId: groupId || undefined,
                  },
                });
              }
            },
          });
        }, 150);
      };
      requestAnimationFrame(tick);
    };

    if (window.TPDirect) {
      run();
      return;
    }
    if (document.querySelector(`script[src="${TAPPAY_SDK}"]`)) {
      // 腳本已加入，可能尚未 onload
      const check = () => (window.TPDirect ? run() : setTimeout(check, 50));
      setTimeout(check, 50);
      return;
    }
    const s = document.createElement('script');
    s.src = TAPPAY_SDK;
    s.onload = () => run();
    document.head.appendChild(s);
  }, [groupId]);

  useEffect(() => {
    if (!payOpen || !selectedPlan || paymentMethod !== 'tappay') return;
    loadAndSetupTapPay();
  }, [payOpen, selectedPlan, paymentMethod, loadAndSetupTapPay]);

  /** 當 onUpdate 在 LIFF/WebView 不觸發時，以 getTappayFieldsStatus 輪詢作為 fallback 更新 canGetPrime、fieldStatus */
  useEffect(() => {
    if (!payOpen || !selectedPlan || paymentMethod !== 'tappay') return;
    const id = setInterval(() => {
      try {
        const st = window.TPDirect?.card?.getTappayFieldsStatus?.();
        if (
          st &&
          typeof (st as { canGetPrime?: boolean }).canGetPrime === 'boolean'
        ) {
          const s = st as {
            canGetPrime: boolean;
            status?: {
              number?: number;
              expiry?: number;
              expirationDate?: number;
              ccv?: number;
            };
          };
          setCanGetPrime(s.canGetPrime);
          setFieldStatus(s.status ?? {});
        }
      } catch {
        // getTappayFieldsStatus 在 card.setup 完成前可能不可用，忽略
      }
    }, 500);
    return () => clearInterval(id);
  }, [payOpen, selectedPlan, paymentMethod]);

  const handleEcPaySubmit = async () => {
    if (!selectedPlan || !subscriptionContext) return;
    if (!invoice.invoiceEmail?.trim()) {
      toast.error('請填寫發票信箱');
      return;
    }
    const jwt = getLiteJwtTokenFromUrlOrStorage();
    if (!jwt) {
      toast.error('請先登入');
      return;
    }
    setIsPaying(true);
    try {
      const inv: PayLiteEcPayInvoice = {
        invoiceType: invoice.invoiceType,
        invoiceEmail: invoice.invoiceEmail.trim(),
      };
      if (invoice.invoiceType === 'company' && invoice.taxId?.trim())
        inv.taxId = invoice.taxId.trim();
      if (invoice.invoiceType === 'company' && invoice.invoiceTitle?.trim())
        inv.invoiceTitle = invoice.invoiceTitle.trim();
      if (invoice.invoiceType === 'personal' && invoice.carrierType)
        inv.carrierType = invoice.carrierType;
      if (
        invoice.invoiceType === 'personal' &&
        (invoice.carrierType === 'cellphone' ||
          invoice.carrierType === 'natural') &&
        invoice.carrierNum?.trim()
      )
        inv.carrierNum = invoice.carrierNum.trim();
      if (invoice.invoiceType === 'donation' && invoice.loveCode?.trim())
        inv.loveCode = invoice.loveCode.trim();

      const raw = liff ? await liff.getIDToken() : null;
      const lineIdToken = raw && typeof raw === 'string' ? raw : undefined;
      const { paymentUrl, formData } = await payLiteSubscriptionEcPay(
        selectedPlan.slug as 'basic' | 'plus' | 'pro',
        jwt,
        lineIdToken,
        inv,
        subscriptionContext
      );
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentUrl;
      for (const [k, v] of Object.entries(formData)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '取得綠界付款表單失敗');
      logErrorToBackend(e instanceof Error ? e : new Error(String(e)), {
        context: {
          event: 'pay_lite_ecpay_failed',
          groupId: groupId || undefined,
        },
      });
    } finally {
      setIsPaying(false);
    }
  };

  const handleConfirmPay = async () => {
    if (!selectedPlan || !subscriptionContext) return;
    const { name, phone_number, email } = cardholder;
    if (!name.trim() || !phone_number.trim() || !email.trim()) {
      toast.error('請填寫持卡人姓名、電話與 Email');
      return;
    }
    const jwt = getLiteJwtTokenFromUrlOrStorage();
    if (!jwt) {
      toast.error('請先登入');
      return;
    }

    setIsPaying(true);
    const logCtx = groupId ? { groupId } : {};
    try {
      const raw = liff ? await liff.getIDToken() : null;
      const lineIdToken = raw && typeof raw === 'string' ? raw : undefined;
      const T = window.TPDirect;
      if (!T?.card?.getPrime) {
        toast.error('金流尚未載入，請稍後再試');
        setIsPaying(false);
        return;
      }

      // 按鈕已依 onUpdate 的 canGetPrime 解鎖，此處僅作防呆；若狀態異常仍阻擋 getPrime
      const status = T.card.getTappayFieldsStatus?.();
      const canGet =
        status &&
        (status.canGetPrime === true ||
          (status as { can_get_prime?: boolean }).can_get_prime === true);
      if (status && !canGet) {
        toast.error('請完整填寫卡號、有效期限與安全碼後再送出');
        setIsPaying(false);
        return;
      }

      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        setIsPaying(false);
        toast.error('取得付款資訊逾時，請重試');
        logErrorToBackend(new Error('TapPay getPrime 逾時'), {
          context: { ...logCtx, event: 'tappay_get_prime_timeout' },
        });
      }, 15000);

      T.card.getPrime(res => {
        clearTimeout(timeoutId);
        if (timedOut) return;
        const prime =
          (res as { prime?: string; card?: { prime?: string } }).prime ??
          (res as { prime?: string; card?: { prime?: string } }).card?.prime;
        if (res.status !== 0 || !prime) {
          const msg = (res as { msg?: string }).msg || '取得付款資訊失敗';
          toast.error(msg);
          logErrorToBackend(new Error(msg), {
            context: {
              ...logCtx,
              event: 'tappay_get_prime_failed',
              status: res.status,
            },
          });
          setIsPaying(false);
          return;
        }
        payLiteSubscription(
          prime,
          selectedPlan.slug as 'basic' | 'plus' | 'pro',
          { name, phone_number, email },
          jwt,
          lineIdToken,
          subscriptionContext
        )
          .then(() => {
            toast.success('訂閱成功');
            setPayOpen(false);
            setSelectedPlan(null);
            fetchStatus();
            handleBack();
          })
          .catch(e => {
            const errMsg = e instanceof Error ? e.message : '付款失敗';
            if (/IP mismatch/i.test(errMsg)) {
              toast.error(
                'IP 白名單不符：請到 TapPay 後台「開發者 > 系統設定」為 Sandbox 加入 0.0.0.0/0 或後端出口 IP。'
              );
            } else {
              toast.error(errMsg);
            }
            logErrorToBackend(e instanceof Error ? e : new Error(String(e)), {
              context: { ...logCtx, event: 'pay_lite_subscription_failed' },
            });
          })
          .finally(() => setIsPaying(false));
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '付款失敗');
      logErrorToBackend(e instanceof Error ? e : new Error(String(e)), {
        context: { ...logCtx, event: 'tappay_confirm_pay_error' },
      });
      setIsPaying(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            <span className="mr-2">←</span>
            <span>返回</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">訂閱方案</h2>
          <p className="text-sm text-gray-600">選擇適合您的方案</p>
        </div>

        {status?.planSlug === 'trial' && status.trialEndsAt && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            試用期至 {formatDateTaipeiSlash(status.trialEndsAt)} 到期，喜歡 LITE
            嗎？立即訂閱以保留完整功能
          </div>
        )}

        <div className="space-y-4">
          {/* 取消訂閱鈕已移至商家資訊卡的「目前方案」右側，點擊後帶 openCancel=1 進入此頁並開啟對話框 */}

          {PLANS.map(plan => (
            <Card key={plan.name} className="border-2 border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <p className="text-xl font-semibold text-gray-900">
                  {plan.price}
                  <span className="text-sm font-normal text-gray-500">
                    {' '}
                    / 月
                  </span>
                </p>
                <p className="text-sm text-gray-600">{plan.publishLimit}</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  {FEATURES.map(f => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="pt-0">
                <button
                  type="button"
                  onClick={() => handleOpenPay(plan)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all text-base min-h-[48px] touch-manipulation"
                >
                  立即訂閱
                </button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      <Dialog
        open={payOpen}
        onOpenChange={open => {
          setPayOpen(open);
          if (!open) {
            // 關閉時重置為綠界付款
            setPaymentMethod('ecpay');
            setInvoice({ invoiceType: 'personal', invoiceEmail: '' });
            setCanGetPrime(false);
            setFieldStatus({});
            hasSetupRef.current = false; // 下次開啟可重新 setup
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedPlan
                ? `訂閱 ${selectedPlan.name} 方案 · ${selectedPlan.price} / 月`
                : '訂閱付款'}
            </DialogTitle>
            <DialogDescription>
              請填寫發票資訊後前往綠界金流頁面完成付款。
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <>
              <div className="py-1">
                <div className="w-full rounded-lg border-2 px-3 py-2 text-sm font-medium text-center border-blue-500 bg-blue-50 text-blue-700">
                  綠界
                </div>
              </div>

              {paymentMethod === 'tappay' && (
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="cardholder-name">持卡人姓名 *</Label>
                    <Input
                      id="cardholder-name"
                      value={cardholder.name}
                      onChange={e =>
                        setCardholder(c => ({ ...c, name: e.target.value }))
                      }
                      placeholder="與信用卡一致"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cardholder-phone">持卡人電話 *</Label>
                    <Input
                      id="cardholder-phone"
                      value={cardholder.phone_number}
                      onChange={e =>
                        setCardholder(c => ({
                          ...c,
                          phone_number: e.target.value,
                        }))
                      }
                      placeholder="0912345678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cardholder-email">持卡人 Email *</Label>
                    <Input
                      id="cardholder-email"
                      type="email"
                      value={cardholder.email}
                      onChange={e =>
                        setCardholder(c => ({ ...c, email: e.target.value }))
                      }
                      placeholder="email@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>卡號</Label>
                    <div
                      id="card-number"
                      className={cn(
                        'block h-10 min-h-10 w-full overflow-hidden rounded-md border border-input bg-background px-3 py-2',
                        'ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                        fieldStatus.number === 2 && 'border-destructive'
                      )}
                      style={{ textTransform: 'none' }}
                    />
                    {fieldStatus.number === 2 && (
                      <p className="text-sm text-destructive">
                        卡號格式不正確；若為測試卡請確認使用 Sandbox 模式
                      </p>
                    )}
                    {fieldStatus.number === 1 && (
                      <p className="text-sm text-amber-600">請完成此欄位</p>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>有效期限</Label>
                      <div
                        id="card-expiration-date"
                        className={cn(
                          'block h-10 min-h-10 w-full overflow-hidden rounded-md border border-input bg-background px-3 py-2',
                          'ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                          (fieldStatus.expiry === 2 ||
                            fieldStatus.expirationDate === 2) &&
                            'border-destructive'
                        )}
                        style={{ textTransform: 'none' }}
                      />
                      {(fieldStatus.expiry === 2 ||
                        fieldStatus.expirationDate === 2) && (
                        <p className="text-sm text-destructive">
                          有效期限格式應為 MM/YY（如 12/30），請勿使用已過期日期
                        </p>
                      )}
                      {(fieldStatus.expiry === 1 ||
                        fieldStatus.expirationDate === 1) && (
                        <p className="text-sm text-amber-600">請完成此欄位</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>安全碼</Label>
                      <div
                        id="card-ccv"
                        className={cn(
                          'block h-10 min-h-10 w-full max-w-[8rem] overflow-hidden rounded-md border border-input bg-background px-3 py-2',
                          'ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                          fieldStatus.ccv === 2 && 'border-destructive'
                        )}
                        style={{ textTransform: 'none' }}
                      />
                      {fieldStatus.ccv === 2 && (
                        <p className="text-sm text-destructive">
                          安全碼應為 3 碼（Visa／Mastercard）或 4 碼（American
                          Express）
                        </p>
                      )}
                      {fieldStatus.ccv === 1 && (
                        <p className="text-sm text-amber-600">請完成此欄位</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === 'ecpay' && (
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>發票類型</Label>
                    <div className="flex flex-wrap gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoiceType"
                          checked={invoice.invoiceType === 'personal'}
                          onChange={() =>
                            setInvoice({ ...invoice, invoiceType: 'personal' })
                          }
                          className="rounded-full"
                        />
                        個人二聯
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoiceType"
                          checked={invoice.invoiceType === 'company'}
                          onChange={() =>
                            setInvoice({ ...invoice, invoiceType: 'company' })
                          }
                          className="rounded-full"
                        />
                        公司三聯
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoiceType"
                          checked={invoice.invoiceType === 'donation'}
                          onChange={() =>
                            setInvoice({ ...invoice, invoiceType: 'donation' })
                          }
                          className="rounded-full"
                        />
                        捐贈
                      </label>
                    </div>
                  </div>

                  {invoice.invoiceType === 'company' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="invoice-taxId">統一編號</Label>
                        <Input
                          id="invoice-taxId"
                          type="text"
                          value={invoice.taxId ?? ''}
                          onChange={e =>
                            setInvoice({ ...invoice, taxId: e.target.value })
                          }
                          placeholder="12345678"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoice-invoiceTitle">發票抬頭</Label>
                        <Input
                          id="invoice-invoiceTitle"
                          type="text"
                          value={invoice.invoiceTitle ?? ''}
                          onChange={e =>
                            setInvoice({
                              ...invoice,
                              invoiceTitle: e.target.value,
                            })
                          }
                          placeholder="公司全名"
                        />
                      </div>
                    </>
                  )}

                  {invoice.invoiceType === 'personal' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="invoice-carrierType">載具類型</Label>
                        <select
                          id="invoice-carrierType"
                          value={invoice.carrierType ?? 'member'}
                          onChange={e =>
                            setInvoice({
                              ...invoice,
                              carrierType: e.target
                                .value as PayLiteEcPayInvoice['carrierType'],
                            })
                          }
                          className={cn(
                            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                          )}
                        >
                          <option value="member">會員載具</option>
                          <option value="cellphone">手機條碼</option>
                          <option value="natural">自然人憑證</option>
                        </select>
                      </div>
                      {(invoice.carrierType === 'cellphone' ||
                        invoice.carrierType === 'natural') && (
                        <div className="space-y-2">
                          <Label htmlFor="invoice-carrierNum">載具號碼</Label>
                          <Input
                            id="invoice-carrierNum"
                            type="text"
                            value={invoice.carrierNum ?? ''}
                            onChange={e =>
                              setInvoice({
                                ...invoice,
                                carrierNum: e.target.value,
                              })
                            }
                            placeholder={
                              invoice.carrierType === 'cellphone'
                                ? '/XXXXXX'
                                : '憑證流水號'
                            }
                          />
                        </div>
                      )}
                    </>
                  )}

                  {invoice.invoiceType === 'donation' && (
                    <div className="space-y-2">
                      <Label htmlFor="invoice-loveCode">愛心碼</Label>
                      <Input
                        id="invoice-loveCode"
                        type="text"
                        value={invoice.loveCode ?? ''}
                        onChange={e =>
                          setInvoice({ ...invoice, loveCode: e.target.value })
                        }
                        placeholder="168001"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="invoice-email">發票信箱 *</Label>
                    <Input
                      id="invoice-email"
                      type="email"
                      value={invoice.invoiceEmail}
                      onChange={e =>
                        setInvoice({
                          ...invoice,
                          invoiceEmail: e.target.value,
                        })
                      }
                      placeholder="invoice@example.com"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <DialogFooter className="relative z-10">
            {paymentMethod === 'tappay' ? (
              <Button
                type="button"
                onClick={handleConfirmPay}
                disabled={
                  isPaying ||
                  !canGetPrime ||
                  !cardholder.name.trim() ||
                  !cardholder.phone_number.trim() ||
                  !cardholder.email.trim()
                }
                className="w-full"
                size="lg"
              >
                {isPaying
                  ? '處理中...'
                  : !canGetPrime
                    ? '請填寫卡號、有效期限與安全碼'
                    : !cardholder.name.trim() ||
                        !cardholder.phone_number.trim() ||
                        !cardholder.email.trim()
                      ? '請填寫持卡人姓名、電話與 Email'
                      : '確認付款'}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleEcPaySubmit}
                disabled={isPaying || !invoice.invoiceEmail?.trim()}
                className="w-full"
                size="lg"
              >
                {isPaying ? '處理中...' : '前往綠界付款'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 取消訂閱確認與回饋表單 */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              確認取消訂閱
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              請協助我們了解原因，以便改進服務。（可選填）
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {CANCEL_REASON_SECTIONS.map(({ section, items }) => (
              <div key={section} className="space-y-2">
                <p className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {section}
                </p>
                <div className="space-y-2.5 pl-1">
                  {items.map(({ code, label }) => (
                    <div key={code}>
                      <label className="flex items-start gap-3 cursor-pointer rounded-lg p-2.5 hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200">
                        <input
                          type="checkbox"
                          checked={cancelReasons.includes(code)}
                          onChange={() => handleToggleCancelReason(code)}
                          className="mt-0.5 w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shrink-0 cursor-pointer"
                        />
                        <span className="text-sm text-gray-800 leading-relaxed flex-1">
                          {label}
                        </span>
                      </label>
                      {/* 缺少功能輸入框直接放在問題選項下方 */}
                      {code === 'missing_feature' &&
                        cancelReasons.includes('missing_feature') && (
                          <div className="mt-2 ml-7 space-y-1.5">
                            <Label
                              htmlFor="cancel-missing-feature"
                              className="text-sm font-medium text-gray-700"
                            >
                              請說明缺少的功能
                            </Label>
                            <Input
                              id="cancel-missing-feature"
                              value={cancelMissingFeature}
                              onChange={e =>
                                setCancelMissingFeature(e.target.value)
                              }
                              placeholder="例如：支援多店、匯出報表…"
                              maxLength={500}
                              className="w-full border-gray-300 focus:border-red-500 focus:ring-red-500"
                            />
                            <p className="text-xs text-gray-500">最多 500 字</p>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {/* 其他原因輸入框 */}
            {cancelReasons.includes('other') && (
              <div className="space-y-1.5 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Label
                  htmlFor="cancel-other-reason"
                  className="text-sm font-medium text-gray-700"
                >
                  其他原因
                </Label>
                <textarea
                  id="cancel-other-reason"
                  value={cancelOtherReason}
                  onChange={e => setCancelOtherReason(e.target.value)}
                  placeholder="請自由填寫"
                  maxLength={1000}
                  rows={3}
                  className={cn(
                    'flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
                    'ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:border-red-500'
                  )}
                />
                <p className="text-xs text-gray-500">最多 1000 字</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={isCancelling}
              className="flex-1 sm:flex-initial"
            >
              返回
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelConfirm}
              disabled={isCancelling}
              className="flex-1 sm:flex-initial"
            >
              {isCancelling ? '處理中...' : '確認取消'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
